import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import Select, { ControlProps, components, StylesConfig } from 'react-select';
import { useTheme } from 'styled-components';

import { useLocalStorage } from '@/hooks/useLocalStorage.hook';

import { StorageKeysEnum } from '@/enums/storageKeys.enum';

import {
  DEFAULT_LANGUAGE,
  countryFlagByName,
  enabledLanguages,
} from '@/i18n/resourses';

type LanguageItemType = {
  value: string;
  label: string;
  image: string;
};

const LangSwitcherOption = ({
  innerProps,
  label,
  data,
}: {
  innerProps: any;
  label: string;
  data: LanguageItemType;
}) => (
  <div
    style={{ display: 'flex', alignItems: 'center', padding: '5px' }}
    {...innerProps}
  >
    <img
      alt={label}
      src={data.image}
      style={{
        borderRadius: '50%',
        height: 'auto',
        marginRight: '5px',
        width: '20px',
      }}
    />
    {label}
  </div>
);

const LangSwitcherControl = ({
  children,
  ...props
}: ControlProps<any, false>) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { lang } = props.selectProps;

  return (
    <components.Control {...props}>
      <img alt={lang} src={lang} style={{ borderRadius: '50%' }} />
      {children}
    </components.Control>
  );
};

const LangSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const { value: lang, setItem: setLang } = useLocalStorage<string>(
    StorageKeysEnum.i18nextLng
  );

  const languageOptions = useMemo<LanguageItemType[]>(
    () =>
      enabledLanguages.map((item: (typeof enabledLanguages)[number]) => ({
        value: item.toLowerCase(),
        label: item.toUpperCase(),
        image: countryFlagByName(item),
      })),
    []
  );

  const defaultLanguage = useMemo(
    () => enabledLanguages[0] || DEFAULT_LANGUAGE,
    []
  );

  const defaultLanguageOption = useMemo(
    () =>
      languageOptions.find((x) => x.value === lang || defaultLanguage) ||
      languageOptions[0],
    [defaultLanguage, lang, languageOptions]
  );

  const [currentLang, setCurrentLang] = useState(defaultLanguageOption);

  const onLangChange = useCallback(
    (opt: LanguageItemType) => {
      setCurrentLang(opt);
      i18n.changeLanguage(opt.value, () => setLang(opt.value));
      window.xtremepush('tag', 'language', opt.value);
      window.xtremepush('tag', 'user_lang', opt.value);
    },
    [i18n, setLang]
  );

  const customStyles = useMemo<StylesConfig<any, false, any>>(
    () => ({
      control: (provided) => ({
        ...provided,
        background: theme.colors.darkBlue,
        borderRadius: '20px',
        border: '0',
        borderColor: theme.colors.darkBlue,
        height: '27px',
        padding: '3px',
        outline: '0',
        boxShadow: 'none',
        cursor: 'pointer',

        img: {
          width: '22px',
          height: '22px',
        },
      }),
      valueContainer: (props) => ({
        ...props,
        padding: '0 6px',

        div: {
          margin: '0',
        },
      }),
      indicatorsContainer: (props) => ({
        ...props,
        div: {
          padding: '0 3px 0 0',
        },
      }),
      dropdownIndicator: (props) => ({
        ...props,
        color: '#F73535',
        ':hover': {
          color: '#F73535',
        },
      }),
      singleValue: (props) => ({
        ...props,
        color: theme.colors.white,
      }),
      option: (props) => ({
        ...props,
        cursor: 'pointer',
        color: theme.colors.white,
      }),
      menuPortal: (provided) => ({
        ...provided,
        border: 'none',
        zIndex: 9999,
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: 1,
        top: '0',
        marginTop: '0px',
        border: 'none',
        boxShadow: 'none',
        color: theme.colors.white,
        background: theme.colors.darkBlue,
        cursor: 'pointer',
      }),
      indicatorSeparator: () => ({ display: 'none' }),
    }),
    [theme.colors]
  );

  return (
    <Select
      components={{
        Option: LangSwitcherOption,
        Control: LangSwitcherControl,
      }}
      defaultValue={currentLang}
      isSearchable={false}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      lang={currentLang.image}
      onChange={onLangChange}
      options={languageOptions}
      styles={customStyles}
      value={languageOptions.find((x) => x.value === currentLang.value)}
    />
  );
};

export default LangSwitcher;
